import styled from 'styled-components';

import {
  grapefruit,
  lima,
  osloGray,
  paradigmLightBlue,
} from '@paradigm/design-system/src/colors';
import Button from '@paradigm/design-system/src/components/Button';

import { BillingSubscription, SubscriptionStatus } from '#/api/billing';

import { Card, SectionTitle, settingsTextPrimary } from './styles';

interface Props {
  readonly subscription: BillingSubscription | null;
  readonly pending: boolean;
  readonly onSubscribe: () => void;
  readonly onManage: () => void;
}

const STATUS_COLOR: Record<SubscriptionStatus, string> = {
  active: lima,
  trialing: lima,
  past_due: grapefruit,
  unpaid: grapefruit,
  incomplete: grapefruit,
  paused: grapefruit,
  canceled: osloGray,
  incomplete_expired: osloGray,
};

// Statuses where the subscription is permanently ended. Show a
// "Re-subscribe" CTA instead of "Manage subscription" — there's
// nothing left to manage on Stripe's side.
const ENDED_STATUSES: ReadonlySet<SubscriptionStatus> = new Set([
  'canceled',
  'incomplete_expired',
]);

// Render the renewal date in UTC. Stripe's `current_period_end` lands on
// a UTC instant; rendering in the user's local zone produces off-by-one
// days for users west of UTC (e.g. `2026-06-01T00:00:00Z` shows as May 31
// in PT). UTC matches the date Stripe charges on.
const formatDate = (iso?: string): string | null => {
  if (iso == null) return null;
  // Pin the locale to en-US: the date sits next to the English "(UTC)"
  // label, and an undefined locale falls back to the host locale, which
  // renders e.g. "1 giu 2026" on a non-English machine.
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

const humanizeStatus = (status: SubscriptionStatus): string =>
  status.replace(/_/gu, ' ');

export default function SubscriptionCard({
  subscription,
  pending,
  onSubscribe,
  onManage,
}: Props) {
  const isEnded =
    subscription !== null && ENDED_STATUSES.has(subscription.status);

  if (subscription === null || isEnded) {
    const message = isEnded
      ? `Your previous subscription was ${humanizeStatus(subscription.status)}.`
      : "You don't have an active subscription.";
    return (
      <Card data-testid="billing-subscription-card">
        <SectionTitle>Subscription</SectionTitle>
        <Body>{message}</Body>
        <Actions>
          <Button
            ariaLabel={isEnded ? 'Re-subscribe' : 'Subscribe'}
            theme="secondary-light-blue"
            size="small"
            disabled={pending}
            onClick={onSubscribe}
          >
            {isEnded ? 'Re-subscribe' : 'Subscribe'}
          </Button>
        </Actions>
      </Card>
    );
  }

  const renews = formatDate(subscription.current_period_end);
  return (
    <Card data-testid="billing-subscription-card">
      <SectionTitle>Subscription</SectionTitle>
      <Status $color={STATUS_COLOR[subscription.status] ?? osloGray}>
        {humanizeStatus(subscription.status)}
      </Status>
      {renews !== null && (
        <Detail>
          Renews {renews} (UTC)
          {subscription.cancel_at_period_end === true &&
            ' — cancels at period end'}
        </Detail>
      )}
      <Actions>
        <Button
          ariaLabel="Manage subscription"
          theme="secondary-dark-blue"
          size="small"
          outline
          disabled={pending}
          onClick={onManage}
        >
          Manage subscription
        </Button>
      </Actions>
    </Card>
  );
}

const Body = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: ${settingsTextPrimary};
`;

const Status = styled.div<{ $color: string }>`
  font-size: 14px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  text-transform: capitalize;
`;

const Detail = styled.div`
  font-size: 11px;
  color: ${paradigmLightBlue};
  opacity: 0.8;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;
